import { quantidadeLinhasTelefone } from '@/lib/quantidadeLinhasTelefone';
import { rotuloTipoChip, produtoTemChip } from '@/lib/tipoChipAtivacao';
import type { LucideIcon } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { useMemo } from 'react';
import { ChevronDown, ChevronUp, CreditCard, MapPin, Phone, XCircle, Smartphone, RefreshCw, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  formatarCPF, 
  formatarTelefone, 
  formatarCEP,
  formatarEnderecoLinhaUnica 
} from '@/utils/formatters';

interface LinhasTelefone {
  ddd?: string;
  linha?: string;
  operadora_doadora?: string | null;
  tipo_chip?: string | null;
  delta?: string | number | null;
  delta_ativo?: string | number | null;
  faixa?: string | number | null;
  tabela?: string | number | null;
  plano_atual?: string | null;
  plano_novo?: string | null;
  valor_atual?: string | number | null;
  valor_novo?: string | number | null;
  ticket_atual?: string | number | null;
  ticket_novo?: string | number | null;
  receita_adicionada?: string | number | null;
}

interface EnderecoInstalacao {
  cep?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  complemento?: string;
}

interface GestorData {
  id?: string;
  nome?: string;
  cpf?: string;
  rg?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
}

interface CessionarioData {
  id?: string;
  nome_completo?: string;
  cpf?: string;
  rg?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
}

interface ResumoObrigatoriosProps {
  item: {
    produto_nome?: string;
    familia?: string;
    quantidade?: number;
    ddd?: string;
    linha?: string;
    tipo_chip?: string;
    operadora_doadora?: string;
    linhas_telefone?: LinhasTelefone[];
    endereco_instalacao?: EnderecoInstalacao | null;
    gestor_id?: string;
    cessionario_id?: string;
    sem_linha_fixa?: boolean; //  Indica se é FIBRA sem linha fixa (apenas internet)
  };
  gestor?: GestorData | null;
  cessionario?: CessionarioData | null;
  expanded?: boolean;
  onExpandClick: () => void;
}

// Tipos de produto por prefixo/nome
type TipoProduto = 'HA' | 'PN' | 'TT' | 'MP' | 'RENOVACAO' | 'FIBRA' | 'FIXA' | 'ENERGIA' | 'TI_CLOUD' | 'APARELHO' | 'OUTRO';

// Componente de campo com indicador
function CampoResumo({ 
  icone, 
  label, 
  valor, 
  obrigatorio = true,
  className
}: { 
  icone: string | LucideIcon; 
  label: string; 
  valor?: string | null; 
  obrigatorio?: boolean;
  className?: string;
}) {
  const preenchido = valor && valor.trim() !== '';
  
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-[10.5px]",
      obrigatorio && !preenchido && "text-destructive",
      className
    )}>
      {/* R1: aceita lucide; emoji legado continua funcionando. */}
      {typeof icone === 'string'
        ? <span>{icone}</span>
        : (() => { const I = icone; return <I className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />; })()}
      <span className="text-[9px] font-bold uppercase tracking-[0.4px] text-muted-foreground/70">{label}</span>
      {preenchido ? (
        <span className="break-all text-[10.5px] font-semibold text-foreground">{valor}</span>
      ) : (
        <XCircle className="h-3 w-3 flex-shrink-0 text-destructive" />
      )}
    </span>
  );
}

// Componente para separador visual
function Separador() {
  return <span className="text-muted-foreground/50 mx-1">│</span>;
}

// Componente para renderizar uma linha telefônica individual
function LinhaResumo({
  index,
  linha,
  mostrarOperadora = false,
  mostrarChip = true,
}: {
  index: number;
  linha: LinhasTelefone;
  mostrarOperadora?: boolean;
  mostrarChip?: boolean;
}) {
  const dddValido = linha.ddd && linha.ddd.length === 2;
  const isNumeroNovo = linha.linha?.toUpperCase() === 'NÚMERO NOVO';
  const linhaValida = isNumeroNovo || (linha.linha && linha.linha.length >= 4);
  const chipValido = linha.tipo_chip && linha.tipo_chip !== '';
  const operadoraValida = linha.operadora_doadora && linha.operadora_doadora !== '';
  const deltaRaw = linha.delta_ativo ?? linha.delta;
  const delta = deltaRaw === null || deltaRaw === undefined || deltaRaw === '' ? null : Number(String(deltaRaw).replace(',', '.'));
  const valorAtualRaw = linha.valor_atual ?? linha.ticket_atual;
  const valorNovoRaw = linha.valor_novo ?? linha.ticket_novo;
  const valorAtual = valorAtualRaw === null || valorAtualRaw === undefined || valorAtualRaw === '' ? null : Number(String(valorAtualRaw).replace(',', '.'));
  const valorNovo = valorNovoRaw === null || valorNovoRaw === undefined || valorNovoRaw === '' ? null : Number(String(valorNovoRaw).replace(',', '.'));

  const formatarNumero = () => {
    if (!linha.ddd && !linha.linha) return null;
    if (isNumeroNovo) return `(${linha.ddd || '??'}) Nº Novo`;
    if (linha.linha) {
      return formatarTelefone(linha.ddd || '', linha.linha);
    }
    return `(${linha.ddd || '??'}) ?????`;
  };

  if (delta !== null && Number.isFinite(delta)) {
    const numero = formatarNumero() || `Linha ${index}`;
    const planos = `${linha.plano_atual || 'Não informado'} → ${linha.plano_novo || 'Não informado'}`;
    const valores = `${valorAtual !== null && Number.isFinite(valorAtual) ? `R$ ${valorAtual.toFixed(2).replace('.', ',')}` : 'Não informado'} → ${valorNovo !== null && Number.isFinite(valorNovo) ? `R$ ${valorNovo.toFixed(2).replace('.', ',')}` : 'Não informado'}`;
    const deltaFaixa = `Δ ${delta > 0 ? '+' : ''}${delta.toFixed(2).replace('.', ',')}%`;
    return (
      <div className="grid min-h-8 grid-cols-[115px_minmax(0,1fr)_auto] items-center gap-2 overflow-hidden text-[10.5px]">
        <span className="truncate font-mono font-semibold text-foreground" title={numero}>{numero}</span>
        <span className="truncate text-foreground" title={planos}><span className="font-bold text-muted-foreground">Plano:</span> {planos}</span>
        <span className="truncate text-right font-semibold tabular-nums text-primary" title={`${valores} · ${deltaFaixa}`}>{valores} · {deltaFaixa}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      <span className="text-muted-foreground font-medium">L{index}:</span>
      
      {/* DDD + Linha */}
      {/* §1:  do chip e  da doadora viram lucide; o  vira XCircle. */}
      {dddValido || linhaValida ? (
        <span className="font-semibold text-foreground">{formatarNumero()}</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-destructive">
          <XCircle className="h-3 w-3" /> DDD/Linha
        </span>
      )}

      {mostrarChip && (
        <>
          <Separador />
          <span className={cn('inline-flex items-center gap-1', !chipValido && 'text-destructive')}>
            <CreditCard className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
            {chipValido ? rotuloTipoChip(linha.tipo_chip) : <XCircle className="h-3 w-3" />}
          </span>
        </>
      )}

      {mostrarOperadora && (
        <>
          <Separador />
          <span className={cn('inline-flex items-center gap-1', !operadoraValida && 'text-destructive')}>
            <RefreshCw className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
            {operadoraValida ? linha.operadora_doadora : <XCircle className="h-3 w-3" />}
          </span>
        </>
      )}
    </div>
  );
}


function CartaoPessoa({ nome, papel, telefone, whatsapp, email, cpf, rg, faltando = [] }: {
  nome?: string; papel: string;
  telefone?: string; whatsapp?: string; email?: string; cpf?: string; rg?: string;
  /** Campos vazios — o cartão continua aparecendo e DIZ o que falta. */
  faltando?: string[];
}) {
  if (!nome && faltando.length === 0) return null;
  if (!nome) {
    return (
      <div className="flex h-8 min-w-0 items-center gap-2 rounded-md border border-dashed border-border bg-card px-2">
        <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{papel}</span>
        <span className="inline-flex min-w-0 items-center gap-1 text-[10px] font-semibold text-destructive">
          <XCircle className="h-3 w-3 flex-shrink-0" />
          <span className="truncate" title={faltando.join(' · ')}>Falta: {faltando.join(' · ')}</span>
        </span>
      </div>
    );
  }
  const contato = whatsapp || telefone || email || '';
  const detalhes = [telefone && `Tel ${telefone}`, whatsapp && `WhatsApp ${whatsapp}`, email, cpf && `CPF ${cpf}`, rg && `RG ${rg}`].filter(Boolean).join(' · ');
  return (
    <div className="flex h-8 min-w-0 items-center gap-2 rounded-md border border-border bg-card px-2" title={detalhes}>
      <span className="shrink-0 rounded border border-border bg-accent px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{papel}</span>
      <CopyOnClick value={nome}>
        <span className="block min-w-0 truncate text-[10.5px] font-semibold text-foreground">{nome}</span>
      </CopyOnClick>
      {contato && <span className="ml-auto hidden max-w-[180px] truncate text-[10px] text-muted-foreground xl:block">{contato}</span>}
      {faltando.length > 0 && <span className="ml-auto shrink-0 text-[9px] font-bold uppercase tracking-wider text-destructive" title={faltando.join(' · ')}>{faltando.length} pend.</span>}
    </div>
  );
}

export function ResumoObrigatorios({ 
  item, 
  gestor, 
  cessionario,
  expanded = false,
  onExpandClick 
}: ResumoObrigatoriosProps) {
  
  // Detectar tipo de produto
  const tipoProduto = useMemo((): TipoProduto => {
    // Normalizar nome: remover NBSP, trim, uppercase
    const nome = (item.produto_nome || '').replace(/\u00A0/g, ' ').trim().toUpperCase();
    const familia = (item.familia || '').toUpperCase();
    
    // Regex para detectar prefixos (HA, PN, TT, MP) no início, seguido de espaço, traço ou fim
    if (/^HA[\s\-–—]?/i.test(nome) || nome === 'HA') return 'HA';
    if (/^PN[\s\-–—]?/i.test(nome) || nome === 'PN' || nome.includes('PORTABILIDADE')) return 'PN';
    if (/^TT[\s\-–—]?/i.test(nome) || nome === 'TT' || nome.includes('TRANSFERENCIA')) return 'TT';
    if (/^MP[\s\-–—]?/i.test(nome) || nome === 'MP' || nome.includes('MIGRACAO')) return 'MP';
    if (/RENOVA[ÇC][AÃ]O/.test(nome)) return 'RENOVACAO';
    if (nome.includes('FIBRA')) return 'FIBRA';
    if (nome.includes('FIXA') || nome.includes('VOZ FIXA')) return 'FIXA';
    if (nome.includes('ENERGIA')) return 'ENERGIA';
    if (nome.includes('TI') || nome.includes('CLOUD') || nome.includes('M2M')) return 'TI_CLOUD';
    if (familia === 'APARELHOS' || familia === 'ACESSORIOS') return 'APARELHO';
    
    return 'OUTRO';
  }, [item.produto_nome, item.familia]);

  // ========== DADOS DO GESTOR (COMPLETO) ==========
  const gestorNome = gestor?.nome || '';
  const gestorCpf = gestor?.cpf ? formatarCPF(gestor.cpf) : '';
  const gestorRg = gestor?.rg || '';
  /* R3 do punch: era `gestor?.whatsapp || gestor?.telefone` — um campo só,
     mostrando o que existisse primeiro. São campos DISTINTOS: o
     calcularPercentualPreenchimento conta os dois separado (um registro pode
     ter um e não o outro) e o WhatsApp é o número em que se manda mensagem.
     Nunca fundir, nunca omitir. */
  const gestorEmail = gestor?.email || '';
  const gestorTelefoneFormatado = gestor?.telefone ? formatarTelefone(gestor.telefone) : '';
  const gestorWhatsappFormatado = gestor?.whatsapp ? formatarTelefone(gestor.whatsapp) : '';

  // ========== DADOS DO CESSIONÁRIO (COMPLETO) ==========
  const cessionarioNome = cessionario?.nome_completo || '';
  const cessionarioCpf = cessionario?.cpf ? formatarCPF(cessionario.cpf) : '';
  const cessionarioRg = cessionario?.rg || '';
  const cessionarioEmail = cessionario?.email || '';
  const cessionarioTelefoneFormatado = cessionario?.telefone ? formatarTelefone(cessionario.telefone) : '';
  const cessionarioWhatsappFormatado = cessionario?.whatsapp ? formatarTelefone(cessionario.whatsapp) : '';
  
  // Verificar se cessionário é igual ao gestor
  const cessionarioIgualGestor = gestor?.cpf && cessionario?.cpf && 
    gestor.cpf.replace(/\D/g, '') === cessionario.cpf.replace(/\D/g, '');

  // ========== DADOS DE LINHAS TELEFÔNICAS ==========
  const linhasArray = useMemo(() => {
    let linhas: LinhasTelefone[] = [];
    
    // Normalizar linhas_telefone (pode vir como string JSON do banco)
    if (item.linhas_telefone) {
      if (typeof item.linhas_telefone === 'string') {
        try {
          linhas = JSON.parse(item.linhas_telefone);
        } catch {
          linhas = [];
        }
      } else if (Array.isArray(item.linhas_telefone)) {
        linhas = item.linhas_telefone;
      }
    }
    
    const quantidadeEsperada = quantidadeLinhasTelefone(item.quantidade || 1, linhas, tipoProduto === 'FIBRA');
    
    // Se não tem linhas no array, criar uma com os campos antigos (fallback)
    if (linhas.length === 0 && (item.ddd || item.linha || item.tipo_chip)) {
      return [{
        ddd: item.ddd,
        linha: item.linha,
        operadora_doadora: item.operadora_doadora,
        tipo_chip: item.tipo_chip
      }];
    }
    
    // Se tem menos linhas que o esperado, preencher com vazias
    const result = [...linhas];
    while (result.length < quantidadeEsperada) {
      result.push({
        ddd: '',
        linha: '',
        operadora_doadora: '',
        tipo_chip: ''
      });
    }
    
    return result;
  }, [item.linhas_telefone, item.quantidade, item.ddd, item.linha, item.operadora_doadora, item.tipo_chip, tipoProduto]);

  // ========== DADOS DE ENDEREÇO (COMPLETO COM CEP) ==========
  const endereco = item.endereco_instalacao;
  const enderecoCompleto = endereco?.logradouro && endereco?.numero && 
    endereco?.bairro && endereco?.cidade && endereco?.uf && endereco?.cep;
  
  // Usar formatarEnderecoLinhaUnica para endereço completo
  const enderecoResumo = endereco ? formatarEnderecoLinhaUnica(endereco) : '';

  // Precisa de operadora doadora?
  const precisaOperadoraDoadora = ['PN', 'TT', 'FIBRA'].includes(tipoProduto);
  const temSnapshotRenovacao = tipoProduto === 'RENOVACAO' && linhasArray.some((linha) =>
    Boolean((linha as unknown as Record<string, unknown>).delta_formula_sign)
      || Object.prototype.hasOwnProperty.call(linha, 'delta_economico')
  );

  // ========== BLOCO GESTOR (2 linhas) ==========
  /* O CARTÃO do gestor vale para TODOS os tipos de produto (HA, MP, FIBRA,
     FIXA, ENERGIA, TI…), não só PN/TT — era por isso que um pedido de HA
     continuava com a fileira corrida enquanto o de PN já tinha o desenho. */
  const renderGestor = () => (
    <CartaoPessoa
      nome={gestorNome}
      papel={cessionarioIgualGestor ? 'Gestor · também é o Cedente' : 'Gestor'}
      telefone={gestorTelefoneFormatado}
      whatsapp={gestorWhatsappFormatado}
      email={gestorEmail}
      cpf={gestorCpf}
      rg={gestorRg}
      faltando={[
        !gestorNome && 'Gestor',
        !gestorCpf && 'CPF',
        !gestorRg && 'RG',
        !gestorTelefoneFormatado && 'Tel',
        !gestorWhatsappFormatado && 'WhatsApp',
        !gestorEmail && 'E-mail',
      ].filter(Boolean) as string[]}
    />
  );

  // ========== BLOCO CEDENTE ==========
  /* renderCessionario saiu: o cedente agora nasce como CartaoPessoa no ramo
     PN/TT, lado a lado com o gestor (desenho 27a). */
  const renderEndereco = () => (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <CampoResumo 
        icone={MapPin} 
        label="Endereço" 
        valor={enderecoCompleto ? enderecoResumo : null} 
      />
    </div>
  );

  // ========== BLOCO LINHAS DETALHADAS ==========
  const renderLinhasDetalhadas = () => (
    <div className="space-y-0.5">
      {linhasArray.map((linha, idx) => (
        <LinhaResumo 
          key={idx} 
          index={idx + 1} 
          linha={linha} 
          mostrarOperadora={precisaOperadoraDoadora}
          // TT mantém o número e o chip do cliente: não há chip a mostrar.
          mostrarChip={!temSnapshotRenovacao && produtoTemChip(item.produto_nome)}
        />
      ))}
    </div>
  );

  // ========== BLOCO DDD + CHIP (para HA com quantidade = 1) ==========
  const renderDddChipSimples = () => {
    const primeiraLinha = linhasArray[0] || {};
    
    // Fallback para campos antigos se linhasArray não tem dados
    const dddValor = primeiraLinha.ddd || item.ddd || '';
    const chipValor = primeiraLinha.tipo_chip || item.tipo_chip || '';
    const linhaValor = primeiraLinha.linha || item.linha || '';
    
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <CampoResumo icone={Phone} label="DDD" valor={dddValor} />
        <Separador />
        <CampoResumo icone={CreditCard} label="Chip" valor={chipValor} />
        {linhaValor && linhaValor.toUpperCase() !== 'NÚMERO NOVO' && (
          <>
            <Separador />
            <CampoResumo 
              icone={Smartphone} 
              label="Linha" 
              valor={formatarTelefone(dddValor, linhaValor)} 
              obrigatorio={false}
            />
          </>
        )}
      </div>
    );
  };

  // Renderizar resumo baseado no tipo de produto
  const renderResumo = () => {
    const quantidadeLinhas = item.quantidade || 1;
    
    switch (tipoProduto) {
      // HA: Gestor completo + DDD + Chip (ou linhas detalhadas se múltiplas)
      case 'HA':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
            {quantidadeLinhas > 1 ? renderLinhasDetalhadas() : renderDddChipSimples()}
          </div>
        );

      // PN/TT: Gestor completo + Cessionário + Linhas detalhadas
      case 'PN':
      case 'TT':
        return (
          <div className="space-y-[9px]">
            {/* Grid de 2 colunas, gap 10 — exatamente o desenho 27a. Quando o
                cedente É o gestor, some o 2º cartão e fica o selo neutro. */}
            <div className={cn('grid gap-2.5', cessionarioIgualGestor ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2')}>
              <CartaoPessoa
                nome={gestorNome}
                papel={cessionarioIgualGestor ? 'Gestor · também é o Cedente' : 'Gestor'}
                telefone={gestorTelefoneFormatado}
                whatsapp={gestorWhatsappFormatado}
                email={gestorEmail}
                cpf={gestorCpf}
                rg={gestorRg}
              />
              {!cessionarioIgualGestor && (
                <CartaoPessoa
                  nome={cessionarioNome}
                  papel="Cedente"
                  telefone={cessionarioTelefoneFormatado}
                  whatsapp={cessionarioWhatsappFormatado}
                  email={cessionarioEmail}
                  cpf={cessionarioCpf}
                  rg={cessionarioRg}
                />
              )}
            </div>
            {renderLinhasDetalhadas()}
          </div>
        );

      // MP: Gestor completo + Linhas (se houver)
      case 'MP':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
            {(item.ddd || item.linha || (item.linhas_telefone && item.linhas_telefone.length > 0)) && (
              quantidadeLinhas > 1 ? renderLinhasDetalhadas() : renderDddChipSimples()
            )}
          </div>
        );

      case 'RENOVACAO':
        return (
          <div className="space-y-1">
            {renderLinhasDetalhadas()}
            {linhasArray.length > 0 && !linhasArray.some((linha) => linha.delta_ativo !== undefined || linha.delta !== undefined) && (
              <span className="inline-flex rounded border border-primary/50 bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-foreground">LEGADO · SEM SNAPSHOT DE DELTA</span>
            )}
          </div>
        );

      // FIBRA: Gestor completo + Endereço + Operadora Doadora (ou "Apenas Internet")
      case 'FIBRA':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
            {renderEndereco()}
            {item.sem_linha_fixa ? (
              // 🌐 FIBRA sem linha fixa - "Apenas Internet"
              <div className="flex items-center gap-1 text-xs text-success dark:text-success">
                <Wifi className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
                <span className="font-medium">Apenas Internet (sem linha fixa)</span>
              </div>
            ) : (
              // 📞 FIBRA com linha fixa - mostrar DDD + Linha + Operadora
              <div className="space-y-0.5">
                {renderLinhasDetalhadas()}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <CampoResumo 
                    icone={RefreshCw} 
                    label="Op. Doadora" 
                    valor={item.operadora_doadora} 
                    obrigatorio={true} 
                  />
                </div>
              </div>
            )}
          </div>
        );

      // FIXA: Gestor completo + DDD + Endereço
      case 'FIXA':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <CampoResumo icone={Phone} label="DDD" valor={linhasArray[0]?.ddd} />
              <Separador />
              <CampoResumo 
                icone={MapPin} 
                label="Endereço" 
                valor={enderecoCompleto ? enderecoResumo : null} 
              />
            </div>
          </div>
        );

      // ENERGIA / TI_CLOUD: Gestor completo + Endereço
      case 'ENERGIA':
      case 'TI_CLOUD':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
            {renderEndereco()}
          </div>
        );

      // APARELHO: Gestor completo
      case 'APARELHO':
        return (
          <div className="space-y-1.5">
            {renderGestor()}
          </div>
        );

      // OUTRO: Gestor genérico completo
      default:
        return (
          <div className="space-y-1.5">
            {renderGestor()}
          </div>
        );
    }
  };

  return (
    <div className="mt-1.5 rounded-md border border-border/60 bg-muted/20 px-2 py-1">
      <div className="flex items-center justify-between gap-2">
        {/* Conteúdo do resumo */}
        <div className="flex-1 min-w-0">
          {renderResumo()}
        </div>
        
        {/* Indicador de expandir/recolher */}
        <button
          type="button"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onExpandClick}
          aria-expanded={expanded}
          aria-label={expanded ? 'Recolher detalhes do item' : 'Abrir detalhes do item'}
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
